import React from 'react';
import PropTypes from 'prop-types';

import { Button, Card, Col, ListGroup, Row } from 'react-bootstrap-5';
import { debounce } from 'lodash';
import { reorderableList } from 'helpers/react_dnd/reorderable_list';

import ConfigurableEnumModal from './form/modal';
import ConfigurableEnumRow from './row';
import { save, reorder } from 'components/configurable_enums/api';

export class ConfigurableEnumIndex extends React.Component {
  static propTypes = {
    basePath: PropTypes.string.isRequired,
    buttonLabel: PropTypes.string,
    enumValues: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      static: PropTypes.bool
    })).isRequired,
    modalText: PropTypes.shape({
      formLabel: PropTypes.string,
      title: PropTypes.string
    }),
    type: PropTypes.string.isRequired
  }

  static defaultProps = {
    modalText: { formLabel: 'Label', title: 'Value' }
  }

  state = {
    enumValues: this.props.enumValues,
    saving: false,
    showModal: false
  }

  showModal = () => {
    this.setState({ showModal: true });
  }

  hideModal = () => {
    this.setState({ showModal: false });
  }

  addEnumValue = () => {
    const { type, basePath } = this.props;
    this.setState({ saving: true });

    save(basePath, { label: 'Untitled', type })
      .then(this.handleCreation)
      .finally(() => this.setState({ saving: false }));
  }

  handleCreation = (enumValue) => {
    this.setState(({ enumValues }) => ({
      showModal: false,
      enumValues: [...enumValues, enumValue]
    }));
  }

  handleSave = (savedEnumValue) => {
    this.setState(({ enumValues }) => {
      enumValues = [...enumValues];

      enumValues.forEach((enumValue, i) => {
        if (enumValue.id === savedEnumValue.id) {
          enumValues[i] = savedEnumValue;
        }
      });

      return { enumValues };
    });
  }

  handleRemoval = (value) => {
    this.setState((state) => {
      const newState = { ...state };

      newState.enumValues = state.enumValues.filter(val => val.id !== value.id);
      newState.saving = false;
      return newState;
    });
  }

  updateReorderedValues = () => {
    reorder(this.props.basePath, this.state.enumValues);
  }

  debouncedUpdateReorderedValues = debounce(this.updateReorderedValues, 1000);

  handleReorder = (dragIndex, hoverIndex) => {
    this.setState((state) => {
      const enumValues = [...state.enumValues];
      const dragValue = enumValues[dragIndex];

      enumValues.splice(dragIndex, 1);
      enumValues.splice(hoverIndex, 0, dragValue);

      this.debouncedUpdateReorderedValues();

      return { enumValues };
    });
  }

  renderValues() {
    const { basePath, modalText } = this.props;
    return this.state.enumValues.map((enumValue, index) => {
      return (
        <ConfigurableEnumRow
          basePath={basePath}
          enumValue={enumValue}
          enumValues={this.state.enumValues}
          index={index}
          key={enumValue.id}
          modalText={modalText}
          onRemove={this.handleRemoval}
          onReorder={this.handleReorder}
          onSave={this.handleSave}
        />
      );
    });
  }

  renderEmpty() {
    return (
      <Card className="dashed-border" name="empty-state">
        <Card.Body>
          <h4 className="text-muted text-center mb-0">
            Using the default values. Add a new value to override the defaults.
          </h4>
        </Card.Body>
      </Card>
    );
  }

  renderHeader() {
    return (
      <Row>
        <Col md={9} xs={12}>
          <h5 className="mt-5 mb-2">{this.props.modalText.title}</h5>
        </Col>
        <Col className="text-end align-self-end mb-2" md={3} xs={12}>
          <Button
            disabled={this.state.saving}
            name="new-value"
            size="sm"
            variant="primary"
            onClick={this.showModal}
          >
            {this.props.buttonLabel || 'Add New Value'}
          </Button>
        </Col>
      </Row>
    );
  }

  renderBody() {
    if (this.state.enumValues.length === 0) {
      return this.renderEmpty();
    }

    return (
      <Card>
        <ListGroup as="ul" className="mb-0" variant="flush">
          {this.renderValues()}
        </ListGroup>
      </Card>
    );
  }

  render() {
    const { basePath, modalText, type } = this.props;
    const { showModal } = this.state;

    return (
      <div>
        {this.renderHeader()}
        {this.renderBody()}

        <ConfigurableEnumModal
          basePath={basePath}
          enumValue={{ label: '', type }}
          formLabel={modalText.formLabel}
          modalTitle={modalText.title}
          show={showModal}
          onCancel={this.hideModal}
          onSave={this.handleCreation}
        />
      </div>
    );
  }
}

export default reorderableList(ConfigurableEnumIndex);
